/**
 * <setbox-faq-tabs> — builds the FAQ's tab bar and switches between panels.
 *
 * Each tab is a `setbox-faq-tab` block that renders its own
 * `[data-faq-panel]` panel carrying the label the merchant typed. The bar is
 * assembled here rather than in Liquid because it sits beside the heading, in
 * the section — and a section reads its nested theme blocks as bare `@theme`
 * entries with no settings, so the labels cannot be reached from there.
 *
 * Panels render visible and are hidden here, so with this script absent the
 * page still holds every question instead of none.
 */
class SetboxFaqTabs extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[role="tablist"]');
    this.panels = Array.from(this.querySelectorAll('[data-faq-panel]'));
    this.emptyMessage = this.querySelector('.setbox-faq__empty');

    // One panel needs no bar: there is nothing to switch to.
    if (!this.bar || this.panels.length < 2) return;

    this.tabs = this.panels.map((panel, index) => {
      const key = panel.dataset.faqPanel;
      const tab = document.createElement('button');
      tab.type = 'button';
      // The Mini landing dresses the same bar as pills, so the class the
      // buttons take is the host's to choose.
      tab.className = this.dataset.tabClass || 'setbox-faq__tab';
      tab.id = `setbox-faq-tab-${key}`;
      tab.dataset.category = key;
      tab.textContent = panel.dataset.tabLabel || '';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', String(index === 0));
      tab.tabIndex = index === 0 ? 0 : -1;

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = -1;

      return tab;
    });

    this.bar.append(...this.tabs);
    this.bar.hidden = false;

    this.bar.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.select(tab.dataset.category);
    });

    // Arrow keys are how a tablist is expected to work; the roving tabindex
    // set in select() keeps Tab itself moving past the bar as one stop.
    this.bar.addEventListener('keydown', (event) => {
      const steps = { ArrowLeft: -1, ArrowRight: 1, Home: 'first', End: 'last' };
      const step = steps[event.key];
      if (step === undefined) return;

      const current = this.tabs.indexOf(event.target.closest('[role="tab"]'));
      if (current === -1) return;

      let next;
      if (step === 'first') next = 0;
      else if (step === 'last') next = this.tabs.length - 1;
      else next = (current + step + this.tabs.length) % this.tabs.length;

      event.preventDefault();
      this.select(this.tabs[next].dataset.category);
      this.tabs[next].focus();
    });

    this.select(this.panels[0].dataset.faqPanel);
  }

  select(category) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.category === category;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    let active = null;
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.faqPanel === category;
      panel.hidden = !isActive;
      if (isActive) active = panel;
    });

    if (this.emptyMessage) {
      this.emptyMessage.hidden = !active || active.querySelectorAll('details').length > 0;
    }
  }
}

if (!customElements.get('setbox-faq-tabs')) {
  customElements.define('setbox-faq-tabs', SetboxFaqTabs);
}
